import { Signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FirebaseApp, FirebaseOptions, initializeApp } from 'firebase/app'
import { Auth, getAuth, getIdToken, signInWithCustomToken, signOut, User, UserCredential } from 'firebase/auth'

import {
  arrayUnion,
  collection,
  collectionGroup,
  CollectionReference,
  deleteDoc,
  deleteField,
  doc,
  DocumentChange,
  DocumentReference,
  DocumentSnapshot,
  endAt,
  FieldPath,
  Firestore,
  getCountFromServer,
  getDoc,
  getDocs,
  getDocsFromServer,
  getFirestore,
  increment,
  limit,
  limitToLast,
  orderBy,
  OrderByDirection,
  PartialWithFieldValue,
  query,
  Query,
  QueryConstraint,
  QueryDocumentSnapshot,
  QuerySnapshot,
  runTransaction,
  serverTimestamp,
  setDoc,
  SetOptions,
  startAfter,
  Timestamp,
  Transaction,
  UpdateData,
  updateDoc,
  where,
  WhereFilterOp,
  WithFieldValue,
  WriteBatch,
  writeBatch,
} from 'firebase/firestore'

import { list, ListenEvent, objectVal } from './rxfire/database'
import { collection as rxFireCollection, collectionChanges as rxFireCollectionChanges, doc as rxFireDoc } from './rxfire/firestore'

import { Database, ref as databaseRef, DatabaseReference, DataSnapshot, getDatabase, onValue, push } from 'firebase/database'

import { Functions, getFunctions, httpsCallable, httpsCallableFromURL, HttpsCallableOptions } from 'firebase/functions'
import { FirebaseStorage, getStorage, list as storageListRef, ListOptions, ListResult, ref as storageRef, StorageReference, uploadBytesResumable, UploadMetadata, UploadTask } from 'firebase/storage'
import { map, Observable } from 'rxjs'

import { Timestamp as LemonTimestamp } from '@lemon/core/model/common'

import { FirestoreProvider } from '@lemon/core-firebase/service/lasku/lasku-tallennus.service'
import { AppCheck, getToken, initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'

import { DestroyRef, inject, signal } from '@angular/core'
import { onSnapshot } from 'firebase/firestore'

export function firestoreDocSignal<T>(refOrQuery: DocumentReference<T, T>, options?: { initialValue: T; map?: (snap: DocumentSnapshot<T>) => T }) {
  const destroyRef = inject(DestroyRef)

  const value = signal<T>(options?.initialValue as T)
  const loading = signal(true)
  const error = signal<string | null>(null)

  const mapFn = options?.map ?? ((snap: DocumentSnapshot<T>) => snap.data())

  const unsub = onSnapshot<T, T>(
    refOrQuery,
    snap => {
      value.set(mapFn(snap))
      loading.set(false)
    },
    err => {
      error.set(err?.message ?? String(err))
      loading.set(false)
    },
  )

  destroyRef.onDestroy(unsub)

  return { value, loading, error }
}

export function firestoreQuerySignal<T>(refOrQuery: Query<T, T>, options?: { initialValue: T[]; map?: (snap: QuerySnapshot<T>) => T[] }) {
  const destroyRef = inject(DestroyRef)

  const value = signal<T[]>(options?.initialValue as T[])
  const loading = signal(true)
  const error = signal<string | null>(null)

  const mapFn = options?.map ?? ((snap: QuerySnapshot<T>) => snap.docs.map(d => d.data()))

  const unsub = onSnapshot<T, T>(
    refOrQuery,
    snap => {
      value.set(mapFn(snap))
      loading.set(false)
    },
    err => {
      error.set(err?.message ?? String(err))
      loading.set(false)
    },
  )

  destroyRef.onDestroy(unsub)

  return { value, loading, error }
}

export interface IFirestoreDocQuery<T> {
  location: string
  listen(): Observable<T>
  listen$(): Signal<T>
  listenSnap(): Observable<DocumentSnapshot<T>>
  get(): Promise<T>
  getSnap(): Promise<DocumentSnapshot<T>>
}

class FirestoreDocQuery<T> implements IFirestoreDocQuery<T> {
  constructor(path: string, firestore: Firestore) {
    this.location = path
    this._firestore = firestore
  }
  location: string
  private _firestore: Firestore
  get(): Promise<T> {
    return this.getSnap().then(snap => snap.data())
  }
  getSnap(): Promise<DocumentSnapshot<T>> {
    const d = doc(this._firestore, this.location)
    return getDoc(d as DocumentReference<T>)
  }
  listen(): Observable<T> {
    return this.listenSnap().pipe(map(snap => snap.data()))
  }
  listen$(): Signal<T> {
    const d = doc(this._firestore, this.location)
    return firestoreDocSignal<T>(d as DocumentReference<T, T>).value
  }
  listenSnap(): Observable<DocumentSnapshot<T>> {
    const d = doc(this._firestore, this.location)
    return rxFireDoc<T>(d as DocumentReference<T>)
  }
}

export interface IFirestoreCollectionQuery<T> {
  location: string

  where(fieldPath: keyof T, opStr: WhereFilterOp, value: unknown): IFirestoreCollectionQuery<T>
  whereFree(fieldPath: string | FieldPath, opStr: WhereFilterOp, value: unknown): IFirestoreCollectionQuery<T>
  limit(n: number): IFirestoreCollectionQuery<T>
  limitToLast(n: number): IFirestoreCollectionQuery<T>
  orderBy(fieldPath: keyof T, directionStr: OrderByDirection): IFirestoreCollectionQuery<T>
  orderByFree(fieldPath: string | FieldPath, directionStr: OrderByDirection): IFirestoreCollectionQuery<T>
  startAfter(after: DocumentSnapshot<T> | LemonTimestamp): IFirestoreCollectionQuery<T>
  endAt(at: DocumentSnapshot<T> | LemonTimestamp): IFirestoreCollectionQuery<T>

  getNativeQuery(): Query<T>
  listen(): Observable<T[]>
  listen$(): Signal<T[]>
  // listenCount(): Observable<number>
  listenSnapshots(): Observable<QueryDocumentSnapshot<T>[]>
  listenSnapshots$(): Signal<QueryDocumentSnapshot<T>[]>
  listenChanges(): Observable<DocumentChange<T>[]>
  listenChanges$(): Signal<DocumentChange<T>[]>
  get(): Promise<T[]>
  getCount(): Promise<number>
  /** Bypass cache */
  getFromServer(): Promise<T[]>
  getSnaps(): Promise<QueryDocumentSnapshot<T>[]>
}

class FirestoreCollectionQuery<T> implements IFirestoreCollectionQuery<T> {
  constructor(path: string, firestore: Firestore) {
    this.location = path
    this._firestore = firestore
  }
  location: string
  private _firestore: Firestore
  private _wheres: QueryConstraint[] = []
  _copy(): FirestoreCollectionQuery<T> {
    const copy = new FirestoreCollectionQuery<T>(this.location, this._firestore)
    copy._wheres = [...this._wheres]
    return copy
  }
  where(fieldPath: keyof T, opStr: WhereFilterOp, value: unknown): IFirestoreCollectionQuery<T> {
    this._wheres.push(where(fieldPath as string, opStr, value))
    return this
  }
  whereFree(fieldPath: string | FieldPath, opStr: WhereFilterOp, value: unknown): IFirestoreCollectionQuery<T> {
    this._wheres.push(where(fieldPath, opStr, value))
    return this
  }
  limit(n: number): IFirestoreCollectionQuery<T> {
    this._wheres.push(limit(n))
    return this
  }
  limitToLast(n: number): IFirestoreCollectionQuery<T> {
    this._wheres.push(limitToLast(n))
    return this
  }
  orderBy(fieldPath: keyof T, directionStr: OrderByDirection): IFirestoreCollectionQuery<T> {
    this._wheres.push(orderBy(fieldPath as string, directionStr))
    return this
  }
  orderByFree(fieldPath: string | FieldPath, directionStr: OrderByDirection): IFirestoreCollectionQuery<T> {
    this._wheres.push(orderBy(fieldPath, directionStr))
    return this
  }
  startAfter(after: DocumentSnapshot<T> | LemonTimestamp): IFirestoreCollectionQuery<T> {
    this._wheres.push(startAfter(after))
    return this
  }
  endAt(at: DocumentSnapshot<T> | LemonTimestamp): IFirestoreCollectionQuery<T> {
    this._wheres.push(endAt(at))
    return this
  }
  /**
   * A `QueryConstraint` is used to narrow the set of documents returned by a
   * Firestore query. `QueryConstraint`s are created by invoking  {@link (startAt:1)}, {@link (startAfter:1)}, {@link endBefore:1}, {@link (endAt:1)} and
   */
  get(): Promise<T[]> {
    return this.getSnaps().then(qsnaps => qsnaps.map(d => d.data()))
  }
  getCount(): Promise<number> {
    return getCountFromServer(this.getNativeQuery()).then(snap => snap.data().count)
  }
  /** Bypass cache */
  getFromServer(): Promise<T[]> {
    return this.getSnapsFromServer().then(qsnaps => qsnaps.map(d => d.data()))
  }
  getNativeQuery(): Query<T, T> {
    const coll = collection(this._firestore, this.location)
    return query<T, T>(coll as CollectionReference<T, T>, ...this._wheres)
  }
  getSnaps(): Promise<QueryDocumentSnapshot<T>[]> {
    return getDocs<T, T>(this.getNativeQuery()).then(qsnap => qsnap.docs)
  }
  getSnapsFromServer(): Promise<QueryDocumentSnapshot<T, T>[]> {
    return getDocsFromServer<T, T>(this.getNativeQuery()).then(qsnap => qsnap.docs)
  }

  listen(): Observable<T[]> {
    return this.listenSnapshots().pipe(map(querySnapshot => querySnapshot.map(a => a.data())))
  }
  listen$(): Signal<T[]> {
    return firestoreQuerySignal(this.getNativeQuery()).value
  }
  // listenCount(): Observable<number> {
  //   return concat(this.getCount(), interval(10000).pipe(
  //     switchMap(() => this.getCount())
  //   ))
  // }
  listenSnapshots(): Observable<QueryDocumentSnapshot<T>[]> {
    return rxFireCollection<T>(this.getNativeQuery())
  }
  listenSnapshots$(): Signal<QueryDocumentSnapshot<T>[]> {
    return toSignal(this.listenSnapshots())
  }
  listenChanges(): Observable<DocumentChange<T>[]> {
    return rxFireCollectionChanges<T>(this.getNativeQuery())
  }
  listenChanges$(): Signal<DocumentChange<T>[]> {
    return toSignal(this.listenChanges())
  }
}

export interface IFirestoreCollectionGroupQuery<T> {
  location: string

  where(fieldPath: keyof T, opStr: WhereFilterOp, value: unknown): IFirestoreCollectionGroupQuery<T>
  whereFree(fieldPath: string | FieldPath, opStr: WhereFilterOp, value: unknown): IFirestoreCollectionGroupQuery<T>
  limit(n: number): IFirestoreCollectionGroupQuery<T>
  limitToLast(n: number): IFirestoreCollectionGroupQuery<T>
  orderBy(fieldPath: keyof T, directionStr: OrderByDirection): IFirestoreCollectionGroupQuery<T>
  orderByFree(fieldPath: string | FieldPath, directionStr: OrderByDirection): IFirestoreCollectionGroupQuery<T>

  getNativeQuery(): Query<T>
  listen(): Observable<T[]>
  listen$(): Signal<T[]>
  // listenCount(): Observable<number>
  listenSnapshots(): Observable<QueryDocumentSnapshot<T>[]>
  listenSnapshots$(): Signal<QueryDocumentSnapshot<T>[]>
  listenChanges(): Observable<DocumentChange<T>[]>
  listenChanges$(): Signal<DocumentChange<T>[]>
  get(): Promise<T[]>
  getCount(): Promise<number>
  getSnaps(): Promise<QueryDocumentSnapshot<T>[]>
}

class FirestoreCollectionGroupQuery<T> implements IFirestoreCollectionGroupQuery<T> {
  constructor(path: string, firestore: Firestore) {
    this.location = path
    this._firestore = firestore
  }
  location: string
  private _firestore: Firestore
  private _wheres: QueryConstraint[] = []
  _copy(): FirestoreCollectionGroupQuery<T> {
    const copy = new FirestoreCollectionGroupQuery<T>(this.location, this._firestore)
    copy._wheres = [...this._wheres]
    return copy
  }
  where(fieldPath: keyof T, opStr: WhereFilterOp, value: unknown): IFirestoreCollectionGroupQuery<T> {
    this._wheres.push(where(fieldPath as string, opStr, value))
    return this
  }
  whereFree(fieldPath: string | FieldPath, opStr: WhereFilterOp, value: unknown): IFirestoreCollectionGroupQuery<T> {
    this._wheres.push(where(fieldPath, opStr, value))
    return this
  }
  limit(n: number): IFirestoreCollectionGroupQuery<T> {
    this._wheres.push(limit(n))
    return this
  }
  limitToLast(n: number): IFirestoreCollectionGroupQuery<T> {
    this._wheres.push(limitToLast(n))
    return this
  }
  orderBy(fieldPath: keyof T, directionStr: OrderByDirection): IFirestoreCollectionGroupQuery<T> {
    this._wheres.push(orderBy(fieldPath as string, directionStr))
    return this
  }
  orderByFree(fieldPath: string | FieldPath, directionStr: OrderByDirection): IFirestoreCollectionGroupQuery<T> {
    this._wheres.push(orderBy(fieldPath, directionStr))
    return this
  }
  getNativeQuery(): Query<T, T> {
    const coll = collectionGroup(this._firestore, this.location)
    return query<T, T>(coll as Query<T, T>, ...this._wheres)
  }
  get(): Promise<T[]> {
    return this.getSnaps().then(qsnaps => qsnaps.map(d => d.data()))
  }
  getCount(): Promise<number> {
    return getCountFromServer(this.getNativeQuery()).then(snap => snap.data().count)
  }
  getSnaps(): Promise<QueryDocumentSnapshot<T, T>[]> {
    return getDocs<T, T>(this.getNativeQuery()).then(qsnap => qsnap.docs)
  }
  listen(): Observable<T[]> {
    return this.listenSnapshots().pipe(map(querySnapshot => querySnapshot.map(a => a.data())))
  }
  listen$(): Signal<T[]> {
    return toSignal(this.listen())
  }
  // listenCount(): Observable<number> {
  //   return concat(this.getCount(), interval(10000).pipe(
  //     switchMap(() => this.getCount())
  //   ))
  // }
  listenSnapshots(): Observable<QueryDocumentSnapshot<T, T>[]> {
    return rxFireCollection<T>(this.getNativeQuery()) as Observable<QueryDocumentSnapshot<T, T>[]>
  }
  listenSnapshots$(): Signal<QueryDocumentSnapshot<T, T>[]> {
    return toSignal(this.listenSnapshots())
  }
  listenChanges(): Observable<DocumentChange<T, T>[]> {
    return rxFireCollectionChanges<T>(this.getNativeQuery()) as Observable<DocumentChange<T, T>[]>
  }
  listenChanges$(): Signal<DocumentChange<T, T>[]> {
    return toSignal(this.listenChanges())
  }
}

export interface IFirestoreBatch {
  set<T>(uri: string, data: WithFieldValue<T>): FirestoreBatch
  set<T>(uri: string, data: PartialWithFieldValue<T>, options: SetOptions): FirestoreBatch
  update<T>(uri: string, data: UpdateData<T>): FirestoreBatch
  updateFieldPath<T>(uri: string, fieldPath: FieldPath, data: UpdateData<T>): FirestoreBatch
  updateRef<T>(ref: DocumentReference<T, T>, data: UpdateData<T>): FirestoreBatch
  updateRefFieldPath<T>(ref: DocumentReference<T, T>, fieldPath: FieldPath, data: UpdateData<T>): FirestoreBatch
  delete(uri: string): FirestoreBatch
  commit(): Promise<void>
}

class FirestoreBatch implements IFirestoreBatch {
  private _batch: WriteBatch
  constructor(private _firestore: Firestore) {
    this._batch = writeBatch(this._firestore)
  }
  set<T>(uri: string, data: WithFieldValue<T>): FirestoreBatch
  set<T>(uri: string, data: PartialWithFieldValue<T>, options: SetOptions): FirestoreBatch
  set<T>(uri: string, data: PartialWithFieldValue<T> | WithFieldValue<T>, options?: SetOptions): FirestoreBatch {
    const document = doc(this._firestore, uri) as DocumentReference<T, T>
    if (options) {
      this._batch.set<T, T>(document, data as PartialWithFieldValue<T>, options)
    } else {
      this._batch.set<T, T>(document, data as WithFieldValue<T>)
    }
    return this
  }
  update<T>(uri: string, data: UpdateData<T>): FirestoreBatch {
    const document = doc(this._firestore, uri) as DocumentReference<T, T>
    this._batch.update<T, T>(document, data)
    return this
  }
  updateRef<T>(ref: DocumentReference<T, T>, data: UpdateData<T>): FirestoreBatch {
    this._batch.update<T, T>(ref, data)
    return this
  }
  updateFieldPath<T>(uri: string, fieldPath: FieldPath, data: UpdateData<T>): FirestoreBatch {
    const document = doc(this._firestore, uri) as DocumentReference<T, T>
    this._batch.update<T, T>(document, fieldPath, data)
    return this
  }
  updateRefFieldPath<T>(ref: DocumentReference<T, T>, fieldPath: FieldPath, data: UpdateData<T>): FirestoreBatch {
    this._batch.update<T, T>(ref, fieldPath, data)
    return this
  }
  delete(uri: string): FirestoreBatch {
    const document = doc(this._firestore, uri) as DocumentReference<unknown>
    this._batch.delete(document)
    return this
  }
  commit(): Promise<void> {
    return this._batch.commit()
  }
}

export type LemonaidFirebaseConfig = FirebaseOptions & { functionsRegion: string; functionsViaOwnDomain?: boolean }

// Firebase Hostingin rewrite katkaisee pyynnön 60 sekunnissa. Raporttitiedosto muodostetaan Lemonatorissa
// synkronisesti (aikaraja 360 s), joten se kutsutaan edelleen suoraan *.cloudfunctions.net-osoitteesta.
const SUORAAN_KUTSUTTAVAT_FUNKTIOT = new Set(['pyydaRaportinTiedostoLemonatorista'])

export abstract class BaseFirebaseService implements StorageUploader {
  app: FirebaseApp
  appCheck: AppCheck

  private _auth: Auth = null
  get auth(): Auth {
    if (!this._auth) {
      this._auth = getAuth(this.app)
    }
    return this._auth
  }

  private _firestore: Firestore = null
  get firestore(): Firestore {
    if (!this._firestore) {
      this._firestore = getFirestore(this.app)
    }
    return this._firestore
  }

  private _firestoreProvider: FirestoreProvider = null
  get firestoreProvider(): FirestoreProvider {
    if (!this._firestoreProvider) {
      this._firestoreProvider = {
        annaDeleteArvo: () => {
          return this.firestoreDeleteMarker()
        },
        annaUusiAvain: () => {
          return this.firestoreCreateId()
        },
        // THIS IS REALLY COUNTERINTUITIVE, BUT THE DOC TYPE CONTAINS NO DATA
        // AND THESE DAYS OUR FRONT END FIREBASE INTEGRATION LIBRARY EXPECTS THE URI,
        // NOT THE DOC TO BE PRESENT!
        annaDoc: (uri: string) => {
          return uri
        },
        annaUusiBatch: () => {
          return this.firestoreBatch()
        },
      }
    }
    return this._firestoreProvider
  }

  private _database: Database = null
  get database(): Database {
    if (!this._database) {
      this._database = getDatabase(this.app)
    }
    return this._database
  }

  private _functions: Functions = null
  get functions(): Functions {
    if (!this._functions) {
      this._functions = getFunctions(this.app, this._options.functionsRegion)
    }
    return this._functions
  }

  constructor(
    private _options: LemonaidFirebaseConfig,
    appName: string,
    recaptchaId: string,
  ) {
    this.app = initializeApp(_options, appName)
    // Create a ReCaptchaEnterpriseProvider instance using your reCAPTCHA Enterprise
    // site key and pass it to initializeAppCheck().
    if (recaptchaId) {
      this.appCheck = initializeAppCheck(this.app, {
        provider: new ReCaptchaEnterpriseProvider(recaptchaId),
        isTokenAutoRefreshEnabled: true, // Set to true to allow auto-refresh.
      })
    }
  }

  databaseRef(path: string): DatabaseReference {
    return databaseRef(this.database, path)
  }

  databaseGet(path: string): Promise<DataSnapshot> {
    const databaseReference = this.databaseRef(path)
    return new Promise((resolve, reject) => {
      onValue(
        databaseReference,
        snap => resolve(snap),
        error => reject(error),
      )
    })
  }

  databaseGetValue<T>(path: string): Promise<T> {
    return this.databaseGet(path).then(snap => snap.val() as T)
  }

  databaseListenValue<T>(path: string, options?: { keyField?: string }): Observable<T> {
    return objectVal<T>(this.databaseRef(path), options)
  }

  databaseListenValueList<T>(path: string, options?: { events?: ListenEvent[] }): Observable<T[]> {
    return list(this.databaseRef(path), options).pipe(map(queryChanges => queryChanges.map(queryChange => queryChange.snapshot.val())))
  }

  databaseCreatePushId(): string {
    return push(databaseRef(this.database)).key
  }

  functionsCall<RequestData = unknown, ResponseData = unknown>(name: string, payload: RequestData, options?: HttpsCallableOptions, region?: 'europe-north1'): Promise<ResponseData> {
    const opts: HttpsCallableOptions = options ? options : { timeout: 3600 * 1000 }
    const functionsInstance = region && region !== this._options.functionsRegion ? getFunctions(this.app, region) : this.functions
    const callable =
      this._options.functionsViaOwnDomain && !SUORAAN_KUTSUTTAVAT_FUNKTIOT.has(name)
        ? httpsCallableFromURL<RequestData, ResponseData>(functionsInstance, `${window.location.origin}/api/${name}`, opts)
        : httpsCallable<RequestData, ResponseData>(functionsInstance, name, opts)
    return callable(payload).then(res => res.data)
  }

  authUserObservable = new Observable<User | null>(sub => {
    this.auth.onAuthStateChanged(
      next => sub.next(next),
      err => sub.error(err),
      () => sub.complete(),
    )
  })

  authSignOut(): Promise<void> {
    return signOut(this._auth)
  }

  authGetIdToken(user: User, forceRefresh?: boolean): Promise<string> {
    return getIdToken(user, forceRefresh)
  }

  async authSignInWithCustomToken(token: string, forceRefreshAppCheck: boolean = true): Promise<UserCredential> {
    await this.appCheckGetToken(forceRefreshAppCheck)
    return signInWithCustomToken(this._auth, token)
  }

  appCheckGetToken(forceRefresh: boolean = false): Promise<string> {
    if (this.appCheck) {
      return getToken(this.appCheck, /* forceRefresh= */ forceRefresh).then(result => result?.token)
    }
  }

  firestoreServerTimestamp(): Timestamp {
    return serverTimestamp() as any as Timestamp
  }

  firestoreTimestampFromDate(date: Date): Timestamp | null {
    if (date) {
      return Timestamp.fromDate(date)
    }
    return null
  }

  firestoreTimestampNew(seconds: number, nanos: number): Timestamp | null {
    return new Timestamp(seconds, nanos)
  }

  firestoreIsTimestamp(obj: any): boolean {
    return obj instanceof Timestamp
  }

  firestoreDeleteMarker(): any {
    return deleteField() as any
  }

  firestoreSetData<T>(path: string, data: T, options?: SetOptions): Promise<void> {
    return setDoc<T, T>(this.firestoreDocRef<T>(path), data, options)
  }

  firestoreUpdateData<T>(path: string, data: T): Promise<void> {
    return updateDoc<T, T>(this.firestoreDocRef<T>(path), data as UpdateData<T>)
  }

  firestoreDeleteDoc(path: string): Promise<void> {
    const d = doc(this.firestore, path)
    return deleteDoc(d)
  }

  firestoreFieldPath(...fieldNames: string[]): FieldPath {
    return new FieldPath(...fieldNames)
  }

  firestoreFieldIncrement(n: number): any {
    return increment(n)
  }

  firestoreUpdateFields<T>(path: string, ...updatedFields: { data: T; fieldPath: FieldPath }[]): Promise<void> {
    if (!updatedFields || updatedFields.length < 1) {
      throw new Error('No update fields specified')
    }

    const first = updatedFields[0]
    if (updatedFields.length === 1) {
      return updateDoc(this.firestoreDocRef<T>(path), first.fieldPath, first.data)
    }

    const copy = [...updatedFields]
    copy.shift() // Poista ensimmäinen
    const rest: (FieldPath | T)[] = []
    for (const entry of copy) {
      rest.push(entry.fieldPath)
      rest.push(entry.data)
    }
    return updateDoc(this.firestoreDocRef<T>(path), first.fieldPath, first.data, ...rest)
  }

  firestoreDocRef<T>(path: string): DocumentReference<T, T> {
    return doc(this.firestore, path) as DocumentReference<T, T>
  }

  firestoreDoc<T>(path: string): IFirestoreDocQuery<T> {
    return new FirestoreDocQuery<T>(path, this.firestore)
  }

  firestoreCollection<T>(path: string): IFirestoreCollectionQuery<T> {
    return new FirestoreCollectionQuery<T>(path, this.firestore)
  }

  firestoreCollectionGroup<T>(group: string): IFirestoreCollectionGroupQuery<T> {
    return new FirestoreCollectionGroupQuery<T>(group, this.firestore)
  }

  firestoreArrayUnion(...elements: unknown[]) {
    return arrayUnion(elements)
  }

  firestoreBatch(): IFirestoreBatch {
    return new FirestoreBatch(this.firestore)
  }

  firestoreCreateId(): string {
    return doc(collection(this._firestore, '_')).id
  }

  firestoreRunTransaction<T>(updateFunction: (transaction: Transaction) => Promise<T>): Promise<T> {
    return runTransaction<T>(this.firestore, updateFunction)
  }

  storage(bucket?: string): FirebaseStorage {
    return getStorage(this.app, bucket)
  }

  storageFile(uri: string, bucket?: string): StorageReference {
    const storage = this.storage(bucket)
    return storageRef(storage, uri)
  }

  storageUpload(uri: string, data: Blob | Uint8Array | ArrayBuffer, metadata?: UploadMetadata, bucket?: string): UploadTask {
    const fileReference = this.storageFile(uri, bucket)
    return uploadBytesResumable(fileReference, data, metadata)
  }

  storageList(uri: string, options?: ListOptions, bucket?: string): Promise<ListResult> {
    return storageListRef(this.storageFile(uri, bucket), options)
  }
}

export interface StorageUploader {
  storageUpload(uri: string, data: Blob | Uint8Array | ArrayBuffer, metadata?: UploadMetadata, bucket?: string): UploadTask
}

export abstract class SharedFirebaseLemonaid extends BaseFirebaseService {}

export abstract class SharedFirebaseCurrentApp extends BaseFirebaseService {}
